/**
 * Utilities for single-metric market modal charts.
 */

/**
 * @typedef {'close' | 'pe' | 'pb' | 'eps' | 'dividendYield' | 'cape' | 'treasury10y'} MetricKey
 */

export const METRIC_DEFS = [
    { key: 'close', label: 'Price', color: 'var(--brand)', valueKey: 'close', kind: 'points' },
    { key: 'pe', label: 'PE', color: '#9C27B0', valueKey: 'pe', kind: 'ratio' },
    { key: 'pb', label: 'P/B', color: '#00BCD4', valueKey: 'pb', kind: 'ratio' },
    { key: 'eps', label: 'EPS', color: '#4CAF50', valueKey: 'eps', kind: 'currency' },
    { key: 'dividendYield', label: 'DivYield', color: '#FF9800', valueKey: 'dividendYield', kind: 'percent' },
    { key: 'cape', label: 'CAPE', color: '#E91E63', valueKey: 'cape', kind: 'ratio' },
    { key: 'treasury10y', label: '10Y', color: '#795548', valueKey: 'yield10y', kind: 'percent' },
];

export const getMetricDef = (metricKey) => METRIC_DEFS.find((m) => m.key === metricKey) || METRIC_DEFS[0];

export const formatMetricValue = (metricKey, value) => {
    if (!Number.isFinite(Number(value))) return '—';
    const n = Number(value);
    const def = getMetricDef(metricKey);
    switch (def.kind) {
        case 'percent':
            return `${n.toFixed(2)}%`;
        case 'currency':
            return n.toFixed(2);
        case 'ratio':
            return n.toFixed(2);
        case 'points':
        default:
            return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
};

export const getSeriesForMetric = ({ metricKey, chartData = [], valuationSeries = null }) => {
    if (!Array.isArray(chartData) || chartData.length === 0) return [];

    if (metricKey === 'close') {
        return chartData
            .map((d) => ({ date: d.date, value: Number(d.close) }))
            .filter((d) => Number.isFinite(d.value));
    }

    const def = getMetricDef(metricKey);
    const seriesData = valuationSeries?.series?.[metricKey];
    if (!Array.isArray(seriesData) || seriesData.length === 0) return [];

    const lookup = {};
    for (const point of seriesData) {
        const date = point?.date;
        if (!date) continue;
        const monthKey = date.substring(0, 7);
        const value = Number(point?.[def.valueKey]);
        lookup[monthKey] = Number.isFinite(value) ? value : null;
    }

    return chartData
        .map((d) => {
            const monthKey = d?.date?.substring(0, 7);
            return {
                date: d.date,
                value: monthKey ? lookup[monthKey] : null,
            };
        })
        .filter((d) => Number.isFinite(d.value));
};

export const getYAxisDomainForSeries = (series = []) => {
    const values = series.map((d) => Number(d.value)).filter((v) => Number.isFinite(v));
    if (!values.length) return ['auto', 'auto'];
    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
        const pad = Math.abs(min || 1) * 0.08;
        return [min - pad, max + pad];
    }

    const pad = (max - min) * 0.1;
    return [min - pad, max + pad];
};
